/**
 * Shared derivations for the WO detail page.
 *
 * Two different time questions live here and they must not be confused.
 * {@link myMinutes} is the signed-in tech's own clock — what "deine Zeit"
 * reports. {@link totalMinutes} is the job's, across every mechanic, and
 * is the only figure an ESTIMATE may be measured against, because the
 * estimate was given for the job and not for one person's share of it.
 *
 * A service is the unit the page shows; the backend still keeps one task
 * under each, which is where the timer, the notes and "done" live. These
 * helpers read the services so the page never has to.
 */
import type { WoDetailService, WorkOrderDetail } from '@/apis/types'
import { taskMinutes, type TimedMinutes } from '@/lib/taskTime'
import { serviceView } from './serviceView'

export { taskMinutes }

/** Services that count toward done/total — one awaiting the customer counts in neither. */
export function countableServices(wo: WorkOrderDetail): WoDetailService[] {
  return wo.services.filter((s) => s.status !== 'PENDING_APPROVAL')
}

/** MY time on this WO — the signed-in tech only, never all mechanics. */
export function myMinutes(wo: WorkOrderDetail, now: number): number {
  return wo.services.filter((s) => s.mine).reduce((sum, s) => sum + taskMinutes(s, now), 0)
}

/** A service's total time (all who worked it) — for its own line. */
export function serviceMinutes(svc: WoDetailService, now: number): number {
  return taskMinutes(svc, now)
}

/**
 * A job's time across EVERYONE who touched it.
 *
 * <p>Deliberately not {@link myMinutes}, and the header needs both. The
 * estimate on a work order was given for the job, so the only honest
 * thing to subtract it from is the job's total: measured against one
 * technician's own clock, a two-person job reports time still in hand
 * long after it has run over. "Deine Zeit" is a different question and
 * keeps its own figure.
 *
 * <p>Takes the SERVICES rather than the work order so the Today feed can
 * call it too — `DoListService` and `WoDetailService` are different wire
 * shapes that agree on exactly the pair this reads.
 */
export function totalMinutes(services: readonly TimedMinutes[], now: number): number {
  return services.reduce((sum, s) => sum + taskMinutes(s, now), 0)
}

/**
 * Is the job running, paused, or has nobody started it?
 *
 * <p>ANYONE's timer, not the reader's — this is the state the budget
 * figure is about, and a job somebody else is actively burning is not
 * "not started" just because the reader is idle. Paused means time has
 * been banked and no clock is running: work began and stopped.
 */
export function timerState(services: readonly TimedMinutes[]): 'running' | 'paused' | 'idle' {
  if (services.some((s) => s.runningSince != null)) return 'running'
  return services.some((s) => s.loggedMinutes > 0) ? 'paused' : 'idle'
}

/**
 * Can the job be handed back? Mirrors the server's finish gate: every
 * workable service done, and awaiting_approval ignored.
 *
 * <p>Counting awaiting_approval here is what used to deadlock the tech —
 * one proposal the manager hadn't answered hid the CTA for good, while
 * the done/total counter (which uses `countableServices`) read "4/4". A
 * proposal is not workable and never bills, so it doesn't hold the car.
 */
export function isAllDone(wo: WorkOrderDetail): boolean {
  const rows = countableServices(wo)
  return rows.length > 0 && rows.every(isServiceDone)
}

/** A proposal is sitting on this WO, unanswered — shown at hand-back.
 *  Asked of the SERVICE, because that is where approval lives. */
export function hasPendingProposal(wo: WorkOrderDetail): boolean {
  return wo.services.some((s) => s.status === 'PENDING_APPROVAL')
}

export function isServiceDone(svc: WoDetailService): boolean {
  return svc.state === 'done'
}

/** The service to auto-expand: my running one — its notes and Finish are
 *  what I came for. A paused or not-yet-started service stays collapsed
 *  until tapped; its Resume / Start sit in the header anyway. */
export function defaultExpandedId(wo: WorkOrderDetail): string | null {
  const running = wo.services.find((svc) => {
    const view = serviceView(svc)
    return view.mine && view.state === 'in_progress'
  })
  return running?.id ?? null
}

/** Does the signed-in tech have a live timer anywhere on this WO? Decides the
 *  clock speed for the page's elapsed labels. */
export function hasMyRunningTask(wo: WorkOrderDetail): boolean {
  return wo.services.some((svc) => svc.state === 'in_progress' && svc.mine)
}

/**
 * The technician already holds a stake in this job — a service of theirs
 * (whatever its state), or the job itself dispatched to them as a whole.
 * Mirrors `hasStake` in `todayGroups.ts` for this tree's shape, which
 * carries `mine` per service rather than a `technicianId`. Once true, the
 * job's whole-job "Take job" claim gives way to a per-service Take on each
 * remaining loose row — see `WorkOrderDetailRoute.tsx` / `WoServiceCard.tsx`.
 */
export function hasStake(wo: WorkOrderDetail, meId: string | null = null): boolean {
  return (meId !== null && wo.assignedTechnicianId === meId) || wo.services.some((s) => s.mine)
}
